/* eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }] */
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { saveAs } from 'file-saver'
import qs from 'query-string'

import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import {
  downloadEcologicalIntegrityReportFile,
  getEcologicalIntegrityReportsRequest
} from '../../../api/reports'
import { showErrorToast } from '../../../utils/toast'
import { buildReportsExpandPath } from '../../../routing/constants'
import ExpandedReportsTable from './ExpandedReportsTable'
import {
  makeEcologicalIntegrityTabColumns,
  makeEcologicalIntegrityTabRow
} from './ecologicalIntegrityTabTable'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import Table from '../../../components/Table'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    classList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const EcologicalIntegrityReportsTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isEcologicalIntegrityLoading,
  setIsEcologicalIntegrityLoading,
  isRunReportTriggered
}) => {
  const history = useNavigate()
  const params = useParams()
  const {
    pagination,
    setPagination,
    selectPage,
    nextPage,
    prevPage,
    nextSection,
    prevSection,
    changeResultsPerPage
  } = usePagination(defaultPaginationState)
  const [ecologicalIntegrityReports, setEcologicalIntegrityReports] = useState([])
  const [ecologicalIntegritySorting, setEcologicalIntegritySorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })
  const prevRunReportTriggered = useRef(isRunReportTriggered)

  const getEcologicalIntegrityReports = async () => {
    if (isEcologicalIntegrityLoading) {
      return
    }
    const ecoPagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    const sorting = {
      sortBy: ecologicalIntegritySorting.sortBy,
      sortDirection: ecologicalIntegritySorting.sortDirection
    }
    setIsEcologicalIntegrityLoading(true)

    try {
      const response = await getEcologicalIntegrityReportsRequest({
        queryStrings: getUrlForAllFilters(filters),
        pagination: qs.stringify(ecoPagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setEcologicalIntegrityReports([])
        setIsEcologicalIntegrityLoading(false)
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      setEcologicalIntegrityReports(response.ecologicalIntegrity || [])
      setPagination(response.pagination || defaultPaginationState)
      setIsEcologicalIntegrityLoading(false)
    } catch (err) {
      setEcologicalIntegrityReports([])
      setIsEcologicalIntegrityLoading(false)
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong', 'showBorder')
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('ecologicalIntegrity'))
  }

  useEffect(() => {
    if (!filters.showReports) {
      return
    }

    const runTriggered = prevRunReportTriggered.current !== isRunReportTriggered
    prevRunReportTriggered.current = isRunReportTriggered

    if (runTriggered && pagination.currentPage !== 1) {
      setPagination((prev) => {
        return { ...prev, currentPage: 1 }
      })
      return
    }

    getEcologicalIntegrityReports()
  }, [
    pagination.currentPage,
    pagination.resultsPerPage,
    ecologicalIntegritySorting.sortBy,
    ecologicalIntegritySorting.sortDirection,
    isRunReportTriggered
  ])

  const downloadCSV = async () => {
    setIsEcologicalIntegrityLoading(true)
    setLoadingScreenMessage('buildingReport')
    const queryString = getUrlForAllFilters(filters)

    try {
      const response = await downloadEcologicalIntegrityReportFile(queryString)

      if (response.error || !response.blob) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsEcologicalIntegrityLoading(false)
      }

      const getDate = new Date().toLocaleDateString('en-GB').replaceAll('/', '_')
      saveAs(response.blob, `Sites_Ecological_Integrity_report_${getDate}.xlsx`)

      setIsEcologicalIntegrityLoading(false)
      setLoadingScreenMessage('runningReport')
    } catch (err) {
      showErrorToast('something go wrong')
      setLoadingScreenMessage('runningReport')
      setIsEcologicalIntegrityLoading(false)
    }
  }

  if (params.title && ecologicalIntegrityReports.length !== 0) {
    return (
      <ExpandedReportsTable
        title={params.title}
        loading={isEcologicalIntegrityLoading}
        rows={makeEcologicalIntegrityTabRow(ecologicalIntegrityReports)}
        columns={makeEcologicalIntegrityTabColumns(
          ecologicalIntegritySorting,
          setEcologicalIntegritySorting,
          ecologicalIntegrityReports
        )}
        totalNumber={pagination.totalNumber}
        resultsPerPage={pagination.resultsPerPage}
        changeResultsPerPage={changeResultsPerPage}
        download={downloadCSV}
      />
    )
  }

  return (
    <div>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id='ecologicalIntegrity' />
          <Dash>{'—'}</Dash>
          <FormattedMessage id='reports' />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest='download' height='32px' marginTop='0px' onClick={downloadCSV}>
            <Download />
            <FormattedMessage id='download' />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab='siteDetails'>
        <Table
          columns={makeEcologicalIntegrityTabColumns(
            ecologicalIntegritySorting,
            setEcologicalIntegritySorting,
            ecologicalIntegrityReports
          )}
          rows={makeEcologicalIntegrityTabRow(ecologicalIntegrityReports)}
          noClearFilter={true}
          noResultMessage='noEcologicalIntegrityMatchFilteringCriteria'
          dataTest='ecologicalIntegrityTab-list'
          isLoading={isEcologicalIntegrityLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest='ecologicalIntegrity-items-per-page'
          menuPlacement='top'
          alternativeOptions={true}
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest='page-number'
          />
        )}
      </ReportsPaginationWrapper>
    </div>
  )
}

export default EcologicalIntegrityReportsTab
